import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import DOMPurify from 'dompurify';

const Testimonials = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonialsData = [
    {
      id: 1,
      name: "Manuel Sojer, PhD",
      position: "Executive Leadership",
      company: "Maschinenfabrik Reinhausen, GmbH",
      content: t('testimonials.testimonial1'),
      rating: 5,
      industry: "AI/ML"
    },
    {
      id: 2,
      name: "Kevin Speer",
      position: "Technology Director, SiC Power Solutions",
      company: "Multinational Semiconductor Manufacturer",
      content: t('testimonials.testimonial2'),
      rating: 5,
      industry: "Semiconductors"
    },
    {
      id: 3,
      name: "Naimish Patel",
      position: "Chief Executive Officer",
      company: "Gridco Systems, Inc.",
      content: t('testimonials.testimonial3'),
      rating: 5,
      industry: "Future Grid"
    },
    {
      id: 4,
      name: "Gary Earle",
      position: "Director of Executive Talent Acquisition",
      company: "$151 Billion Multinational",
      content: t('testimonials.testimonial4'),
      rating: 5,
      industry: "Power Electronics"
    },
    {
      id: 5,
      name: "Phil Charatz",
      position: "President, Americas",
      company: "Fuji Electric Corporation of America",
      content: t('testimonials.testimonial5'),
      rating: 5,
      industry: "Power Electronics"
    },
    {
      id: 6,
      name: "George Kirby, P.E.",
      position: "Director of Sales",
      company: "American Superconductor",
      content: t('testimonials.testimonial6'),
      rating: 5,
      industry: "Semiconductors"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonialsData.length]);

  const goToNext = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
  }, [testimonialsData.length]);

  const goToPrev = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
  }, [testimonialsData.length]);

  const goToSlide = useCallback((index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  }, []);

  const currentTestimonial = testimonialsData[currentIndex];

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-50 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {t('testimonials.title')} <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">{t('testimonials.titleHighlight')}</span> {t('testimonials.titleEnd')}
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Carousel Container */}
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            {/* Quote Section - Above Everything */}
            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 lg:p-8 border-2 border-gray-300 shadow-lg mb-6">
              {/* Quote Icon */}
              <Quote className="w-10 h-10 text-cyan-600/50 mb-4" />
              
              {/* Quote Text */}
              <p 
                className="text-lg lg:text-xl text-slate-900 leading-relaxed font-medium mb-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`"${currentTestimonial.content}"`) }}
              />

              {/* Rating */}
              <div className="flex items-center space-x-1">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <Star key={`star-${currentTestimonial.id}-${i}`} className="w-5 h-5 fill-cyan-500 text-cyan-600" />
                ))}
              </div>
            </div>

            {/* Background Image Section with Name/Title */}
            <div className="relative rounded-2xl overflow-hidden mb-8 min-h-[480px] bg-transparent">
              {/* Background Image */}
              {currentIndex === 0 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/2lwol5kw_IMG_8707%202.JPG)',
                    backgroundPosition: 'center bottom',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 1 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/r3xxw80o_IMG_8708%202.JPG)',
                    backgroundPosition: 'center bottom',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 2 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/mcdccqz5_Screen%20Shot%202026-04-01%20at%2012.33.06%20PM.png)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 3 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/1mk0agvu_Screen%20Shot%202026-04-01%20at%2012.33.53%20PM.png)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 4 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/t12dxx9h_IMG_8698.JPG)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 5 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/sy0d7dej_IMG_8697.JPG)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 6 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/idcchfcm_IMG_8701.JPG)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              {currentIndex === 7 && (
                <div 
                  className="absolute inset-0 bg-cover z-20"
                  style={{ 
                    backgroundImage: 'url(https://customer-assets.emergentagent.com/job_neon-engineers/artifacts/2lwol5kw_IMG_8707%202.JPG)',
                    backgroundPosition: 'center center',
                    opacity: 1.0
                  }}
                />
              )}
              
              {/* Content Container - Name/Title on top layer, positioned at top */}
              <div className="absolute top-4 left-4 right-8 lg:left-4 lg:right-12 z-30">
                {/* Name & Title Box - Left (On top of image) */}
                <div className="border-2 border-gray-400 rounded-xl p-4 bg-white/95 backdrop-blur-sm shadow-lg max-w-[313px]">
                  <div className="text-left">
                    <div className="text-xl font-bold text-slate-900 mb-1">
                      {currentTestimonial.name}
                    </div>
                    <div className="text-sm text-slate-700 font-medium mb-1">
                      {currentTestimonial.position}
                    </div>
                    <div className="text-sm text-cyan-700 font-bold">
                      {currentTestimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 bg-white/90 border border-cyan-900/50 rounded-full flex items-center justify-center hover:bg-slate-700 hover:border-cyan-600 transition-all duration-300 group"
              aria-label={t('testimonials.prevButton')}
            >
              <ChevronLeft className="w-6 h-6 text-slate-700 group-hover:text-cyan-700 transition-colors" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 bg-white/90 border border-cyan-900/50 rounded-full flex items-center justify-center hover:bg-slate-700 hover:border-cyan-600 transition-all duration-300 group"
              aria-label={t('testimonials.nextButton')}
            >
              <ChevronRight className="w-6 h-6 text-slate-700 group-hover:text-cyan-700 transition-colors" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className="flex items-center justify-center space-x-2 mt-8">
            {testimonialsData.map((testimonial) => (
              <button
                key={`dot-${testimonial.id}`}
                onClick={() => goToSlide(testimonialsData.indexOf(testimonial))}
                className={`h-2 rounded-full transition-all duration-300 ${
                  testimonialsData.indexOf(testimonial) === currentIndex 
                    ? 'w-8 bg-cyan-500' 
                    : 'w-2 bg-slate-700 hover:bg-slate-600'
                }`}
                aria-label={`Go to testimonial ${testimonialsData.indexOf(testimonial) + 1}`}
              />
            ))}
          </div>

          {/* Counter */}
          <div className="text-center mt-4 text-sm text-gray-500">
            {currentIndex + 1} / {testimonialsData.length}
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-5xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { id: 'stat-companies', value: t('testimonials.stat2Value'), label: t('testimonials.stat2'), sublabel: 'From startups to Fortune 500' },
            { id: 'stat-placements', value: t('testimonials.stat1Value'), label: t('testimonials.stat1'), sublabel: 'Across all technical domains' },
            { id: 'stat-satisfaction', value: '98%', label: 'Client Satisfaction', sublabel: 'Would recommend to peers' }
          ].map((stat) => (
            <div 
              key={stat.id}
              className="text-center p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-cyan-600 transition-all duration-300 shadow-sm"
            >
              <div className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-slate-900 font-bold mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-slate-700">
                {stat.sublabel}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
